import React, { useState, useEffect } from 'react';
import { Container, Typography, Divider, Pagination } from '@mui/material';
import { notifyError } from '../../utils/Api';

import EnvioForm from './EnvioForm';
import CasilleroForm from './CasilleroRegister';
import Filters from './Filters';
import EnviosTimeline from './EnviosTimeline';
import HistorialTimeline from './HistorialTimeline';
import EnvioConsulta from './EnvioConsulta';

import { useEnvios } from '../../hooks/useEnvios';
import { useCasilleros } from '../../hooks/useCasilleros';
import { useHistorial } from '../../hooks/useHistorial';
import { useCrearEnvio } from '../../hooks/useCrearEnvio';

export default function UserDashboard() {
  const rol = (localStorage.getItem('rol') || '').trim().toLowerCase();
  const isAdmin = rol === 'admin' || rol === 'superadmin';
  const isCliente = rol === 'cliente';

  const [formData, setFormData] = useState({
    numeroGuia: '',
    descripcion: '',
    origen: '',
    destino: '',
    tipoEnvio: '',
    casilleroCodigo: '',
    pesoLibras: ''
  });

  const [casilleroForm, setCasilleroForm] = useState({
    nombre: '',
    correo: '',
    telefono: '',
    direccion: '',
    descripcion: ''
  });

  const formValidEnvio =
    formData.numeroGuia.trim() &&
    formData.descripcion.trim() &&
    formData.origen.trim() &&
    formData.destino.trim() &&
    formData.tipoEnvio &&
    formData.casilleroCodigo.trim() &&
    Number(formData.pesoLibras) > 0;

  const formValidCasillero =
    casilleroForm.nombre.trim() &&
    casilleroForm.correo.trim() &&
    casilleroForm.descripcion.trim();

  const [search, setSearch] = useState('');
  const [filterTipo, setFilterTipo] = useState('');
  const [consultaGuia, setConsultaGuia] = useState('');
  const [estado, setEstado] = useState('');

  // 🔹 Paginación
  const [page, setPage] = useState(1);
  const limit = 20;

  const {
    envios,
    totalEnvios,
    fetchEnvios,
    fetchMisEnvios,
    fetchEnviosPorTipo,
    envioConsultado,
    consultarEnvio,
    loadingList,
  } = useEnvios(isAdmin, notifyError);

  const {
    casilleros,
    fetchCasilleros,
    createCasillero,
    loadingCreateCasillero,
    fetchMiCasillero
  } = useCasilleros(notifyError, isAdmin);

  const {
    historial,
    fetchHistorial,
    updateEstado,
    loadingConsulta,
    loadingUpdate
  } = useHistorial(notifyError);

  const { createEnvio, loadingCreate, mensaje } = useCrearEnvio(notifyError);

  const [miCasillero, setMiCasillero] = useState(null);

  useEffect(() => {
    let mounted = true;

    const initForCliente = async () => {
      try {
        const casillero = await fetchMiCasillero();
        if (!mounted) return;
        setMiCasillero(casillero);

        if (casillero) {
          if (typeof fetchMisEnvios === 'function') {
            await fetchMisEnvios(page, limit);
          } else {
            try {
              await fetchEnvios(page, limit);
            } catch (e) {
              console.warn('[UserDashboard] fallback fetchEnvios falló:', e);
            }
          }
        }
      } catch (err) {
        if (!mounted) return;
        notifyError(err.message || 'Error al obtener casillero');
      }
    };

    if (isAdmin) {
      fetchEnvios(page, limit);
      fetchCasilleros();
    } else if (isCliente) {
      initForCliente();
    }

    return () => {
      mounted = false;
    };
  }, [isAdmin, isCliente, fetchEnvios, fetchCasilleros, fetchMiCasillero, fetchMisEnvios, page, limit]);

  // 🔹 Nuevo efecto: aplicar filtro por tipo con paginación
  useEffect(() => {
    if (!isAdmin) return;
    if (filterTipo) {
      fetchEnviosPorTipo(filterTipo, page, limit);
    } else {
      fetchEnvios(page, limit);
    }
  }, [filterTipo, page, isAdmin, fetchEnvios, fetchEnviosPorTipo, limit]);

  const handleCrearEnvio = () => {
    if (!formValidEnvio) return;
    const payload = {
      ...formData,
      tipoEnvio: formData.tipoEnvio.trim().toLowerCase(),
      casilleroCodigo: formData.casilleroCodigo,
      pesoLibras: Number(formData.pesoLibras)
    };
    createEnvio(payload, () =>
      setFormData({
        numeroGuia: '',
        descripcion: '',
        origen: '',
        destino: '',
        tipoEnvio: '',
        casilleroCodigo: '',
        pesoLibras: ''
      })
    );
  };

  const handleCrearCasillero = () => {
    if (!formValidCasillero) return;
    createCasillero(casilleroForm, (nuevo) => {
      setCasilleroForm({
        nombre: '',
        correo: '',
        telefono: '',
        direccion: '',
        descripcion: ''
      });
      setMiCasillero(nuevo);
    });
  };

  const handleConsultarEnvio = () => {
    if (!consultaGuia.trim()) return;
    consultarEnvio(consultaGuia);
  };

  const handleActualizarEstado = () => {
    if (!envioConsultado?.id || !estado.trim()) return;
    updateEstado(envioConsultado.id, estado);
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Typography variant="h4">🚚 Dashboard de Envíos</Typography>

      {isCliente && (
        <>
          <Typography sx={{ mb: 2 }}>
            Casillero: {miCasillero?.codigo || 'No tienes casillero aún'}
          </Typography>

          {!miCasillero && (
            <>
              <CasilleroForm
                formData={casilleroForm}
                setFormData={setCasilleroForm}
                onSubmit={handleCrearCasillero}
                loadingCreate={loadingCreateCasillero}
                formValid={!!formValidCasillero}
              />
              <Divider sx={{ my: 3 }} />
            </>
          )}

          <EnviosTimeline
            envios={envios}
            isAdmin={false}
            onSelect={(id) => fetchHistorial(id)}
            loading={loadingList}
          />
        </>
      )}

      {isAdmin && (
        <>
          <EnvioForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleCrearEnvio}
            casilleros={casilleros}
            loadingCreate={loadingCreate}
            formValid={!!formValidEnvio}
            mensaje={mensaje}
          />
          <Divider sx={{ my: 3 }} />

          <Filters
            filterTipo={filterTipo}
            setFilterTipo={setFilterTipo}
            search={search}
            setSearch={setSearch}
            onClear={() => {
              setFilterTipo('');
              setSearch('');
              setPage(1);
            }}
            isAdmin={isAdmin}
          />

          <EnviosTimeline
            envios={envios.filter(
              (e) =>
                (!search ||
                  (e.numeroGuia || '').toLowerCase().includes(search.toLowerCase()))
            )}
            isAdmin={true}
            onSelect={(id) => fetchHistorial(id)}
            loading={loadingList}
          />

          {/* 🔹 Paginador */}
          <Pagination
            count={Math.ceil(totalEnvios / limit)}
            page={page}
            onChange={(e, value) => setPage(value)}
            sx={{ mt: 2, mb: 2 }}
          />

          <EnvioConsulta
            consultaGuia={consultaGuia}
            setConsultaGuia={setConsultaGuia}
            envioConsultado={envioConsultado}
            onConsultar={handleConsultarEnvio}
            historial={historial}
            onCerrar={() => setConsultaGuia('')}
            isAdmin={true}
            estado={estado}
            setEstado={setEstado}
            onActualizarEstado={handleActualizarEstado}
            loadingConsulta={loadingConsulta}
            loadingActualizar={loadingUpdate}
          />
        </>
      )}

      {envioConsultado && <HistorialTimeline historial={historial} />}
    </Container>
  );
}